// src/api/businessApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Determine the API base URL based on environment
const getApiBaseUrl = () => {
  // In production, use the environment variable
  if (process.env.NODE_ENV === "production") {
    return (
      process.env.REACT_APP_API_URL ||
      "https://tr-api.chicagojamaat.org/api"
    );
  }
  // In development, use localhost
  return "http://localhost:5002/api";
};

export const businessApi = createApi({
  reducerPath: "businessApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers) => {
      // Get admin token for authentication
      const adminToken = localStorage.getItem("adminToken");
      console.log("Admin token:", adminToken);
      if (adminToken) {
        headers.set("Authorization", `Bearer ${adminToken}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Business", "Admin", "Contact", "Newsletter", "Item", "Media", "Resource", "Event", "PageContent", "Form", "FormSubmission"],
  endpoints: (builder) => ({
    // Admin Login
    adminLogin: builder.mutation({
      query: (body) => ({
        url: "/admin/login",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Admin"],
    }),

    // Admin Management
    getAdmins: builder.query({
      query: () => ({
        url: "/admin",
        method: "GET",
      }),
      providesTags: ["Admin"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch admins",
      }),
    }),

    getIndustries: builder.query({
      query: () => ({
        url: "/admin/industries",
        method: "GET",
      }),
      providesTags: ["Admin"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch industries",
      }),
    }),

    createAdmin: builder.mutation({
      query: (adminData) => ({
        url: "/admin",
        method: "POST",
        body: adminData,
      }),
      invalidatesTags: ["Admin"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to create admin",
      }),
    }),

    getAdminById: builder.query({
      query: (id) => ({
        url: `/admin/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Admin", id }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch admin",
      }),
    }),

    updateAdmin: builder.mutation({
      query: ({ id, ...updates }) => ({
        url: `/admin/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Admin", id },
        "Admin",
      ],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to update admin",
      }),
    }),

    deleteAdmin: builder.mutation({
      query: (id) => ({
        url: `/admin/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Admin", id }, "Admin"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to delete admin",
      }),
    }),

    // Business CRUD Operations
    getBusinesses: builder.query({
      query: ({ page = 1, limit = 0, search } = {}) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);

        return {
          url: `/businesses?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Business"],
      transformErrorResponse: (response) => {
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to fetch businesses" };
      },
    }),

    getSponserBusinesses: builder.query({
      query: ({ page = 1, limit = 0, search } = {}) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);

        return {
          url: `/businesses/sponsored?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Business"],
      transformErrorResponse: (response) => {
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to fetch businesses" };
      },
    }),

    getBusinessById: builder.query({
      query: (id) => ({
        url: `/businesses/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Business", id }],
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return { message: "Business not found" };
        }
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to fetch business" };
      },
    }),

    getBusinessByIts: builder.query({
      query: (itsNumber) => ({
        url: `/businesses/its/${itsNumber}`,
        method: "GET",
      }),
      providesTags: (result, error, itsNumber) => [{ type: "Business", id: `its-${itsNumber}` }],
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return { message: "Business not found" };
        }
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to fetch business" };
      },
    }),

    createBusiness: builder.mutation({
      query: (body) => ({
        url: "/businesses",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Business"],
      transformErrorResponse: (response) => {
        if (response.status === 400) {
          return (
            response.data || {
              message: "Validation error. Please check your input.",
            }
          );
        }
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to create business" };
      },
    }),

    updateBusiness: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/businesses/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Business", id },
        "Business",
      ],
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return { message: "Business not found" };
        }
        if (response.status === 400) {
          return (
            response.data || {
              message: "Validation error. Please check your input.",
            }
          );
        }
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to update business" };
      },
    }),

    deleteBusiness: builder.mutation({
      query: (id) => ({
        url: `/businesses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Business", id },
        "Business",
      ],
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return { message: "Business not found" };
        }
        if (response.status === 401) {
          return { message: "Unauthorized. Please login again." };
        }
        return response.data || { message: "Failed to delete business" };
      },
    }),

    // Contact form endpoints
    submitContactForm: builder.mutation({
      query: (contactData) => ({
        url: "/contact",
        method: "POST",
        body: contactData,
      }),
      transformErrorResponse: (response) => {
        if (response.status === 400) {
          return response.data || { message: "Invalid contact form data" };
        }
        return response.data || { message: "Failed to submit contact form" };
      },
    }),

    // Get all contacts with optional filters and pagination (admin only)
    getContacts: builder.query({
      query: ({ page = 1, limit = 10, status, search } = {}) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (status) params.append("status", status);
        if (search) params.append("search", search);

        return {
          url: `/contact?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Contact"],
      transformErrorResponse: (response) => {
        return {
          status: response.status,
          message: response.data?.message || "Failed to fetch contacts",
        };
      },
    }),
    // Get contact by ID (admin only)
    getContactById: builder.query({
      query: (id) => ({
        url: `/contact/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Contact", id }],
      transformErrorResponse: (response) => {
        return {
          status: response.status,
          message: response.data?.message || "Failed to fetch contact",
        };
      },
    }),

    // Update contact status (admin only)
    updateContactStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/contact/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Contact", id },
        "Contact",
      ],
      transformErrorResponse: (response) => {
        return {
          status: response.status,
          message: response.data?.message || "Failed to update contact status",
        };
      },
    }),

    // Delete contact (admin only)
    deleteContact: builder.mutation({
      query: (id) => ({
        url: `/contact/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Contact", id },
        "Contact",
      ],
      transformErrorResponse: (response) => {
        return {
          status: response.status,
          message: response.data?.message || "Failed to delete contact",
        };
      },
    }),

    // Media Library Operations
    getMedia: builder.query({
      query: ({ page = 1, limit = 20, folder, mime_type, search } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (folder) params.append("folder", folder);
        if (mime_type) params.append("mime_type", mime_type);
        if (search) params.append("search", search);
        return { url: `/media?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Media"],
    }),

    getMediaById: builder.query({
      query: (id) => ({ url: `/media/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Media", id }],
    }),

    getMediaFolders: builder.query({
      query: () => ({ url: "/media/folders", method: "GET" }),
      providesTags: ["Media"],
    }),

    uploadMedia: builder.mutation({
      query: (formData) => ({
        url: "/media/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Media"],
    }),

    updateMedia: builder.mutation({
      query: ({ id, ...updates }) => ({
        url: `/media/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Media", id }, "Media"],
    }),

    deleteMedia: builder.mutation({
      query: (id) => ({ url: `/media/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [{ type: "Media", id }, "Media"],
    }),

    // Resource Operations (extends newsletters with content_type)
    getResources: builder.query({
      query: ({ page = 1, limit = 10, status, content_type, category, search } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (content_type) params.append("content_type", content_type);
        if (category) params.append("category", category);
        if (search) params.append("search", search);
        return { url: `/resources?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Resource", "Newsletter"],
    }),

    getResourcesByType: builder.query({
      query: ({ type, page = 1, limit = 10, status = "published" } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        return { url: `/resources/type/${type}?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Resource"],
    }),

    getResourceBySlug: builder.query({
      query: (slug) => ({ url: `/resources/slug/${slug}`, method: "GET" }),
      providesTags: (result, error, slug) => [{ type: "Resource", id: slug }],
    }),

    getContentTypeCounts: builder.query({
      query: () => ({ url: "/resources/counts", method: "GET" }),
      providesTags: ["Resource"],
    }),

    getFeaturedResources: builder.query({
      query: ({ limit = 5, content_type } = {}) => {
        const params = new URLSearchParams();
        params.append("limit", limit);
        if (content_type) params.append("content_type", content_type);
        return { url: `/resources/featured?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Resource"],
    }),

    createResource: builder.mutation({
      query: (data) => ({ url: "/resources", method: "POST", body: data }),
      invalidatesTags: ["Resource", "Newsletter"],
    }),

    getResourceById: builder.query({
      query: (id) => ({ url: `/resources/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Resource", id }],
    }),

    updateResource: builder.mutation({
      query: ({ id, ...updates }) => ({ url: `/resources/${id}`, method: "PUT", body: updates }),
      invalidatesTags: (result, error, { id }) => [{ type: "Resource", id }, "Resource", "Newsletter"],
    }),

    deleteResource: builder.mutation({
      query: (id) => ({ url: `/resources/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [{ type: "Resource", id }, "Resource", "Newsletter"],
    }),

    // Event Operations
    getEvents: builder.query({
      query: ({ page = 1, limit = 10, status, type, search } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (type) params.append("type", type);
        if (search) params.append("search", search);
        return { url: `/events?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch events",
      }),
    }),

    getUpcomingEvents: builder.query({
      query: ({ limit = 10 } = {}) => {
        const params = new URLSearchParams();
        params.append("limit", limit);
        return { url: `/events/upcoming?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch upcoming events",
      }),
    }),

    getFeaturedEvents: builder.query({
      query: ({ limit = 5 } = {}) => {
        const params = new URLSearchParams();
        params.append("limit", limit);
        return { url: `/events/featured?${params.toString()}`, method: "GET" };
      },
      providesTags: ["Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch featured events",
      }),
    }),

    getEventBySlug: builder.query({
      query: (slug) => ({ url: `/events/slug/${slug}`, method: "GET" }),
      providesTags: (result, error, slug) => [{ type: "Event", id: slug }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch event",
      }),
    }),

    getEventById: builder.query({
      query: (id) => ({ url: `/events/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Event", id }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch event",
      }),
    }),

    createEvent: builder.mutation({
      query: (data) => ({ url: "/events", method: "POST", body: data }),
      invalidatesTags: ["Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to create event",
      }),
    }),

    updateEvent: builder.mutation({
      query: ({ id, ...updates }) => ({ url: `/events/${id}`, method: "PUT", body: updates }),
      invalidatesTags: (result, error, { id }) => [{ type: "Event", id }, "Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to update event",
      }),
    }),

    deleteEvent: builder.mutation({
      query: (id) => ({ url: `/events/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [{ type: "Event", id }, "Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to delete event",
      }),
    }),

    rsvpToEvent: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/events/${id}/rsvp`, method: "POST", body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: "Event", id }, "Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to RSVP",
      }),
    }),

    getEventRsvps: builder.query({
      query: (id) => ({ url: `/events/${id}/rsvps`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Event", id: `rsvps-${id}` }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch RSVPs",
      }),
    }),

    cancelRsvp: builder.mutation({
      query: ({ eventId, rsvpId }) => ({ url: `/events/${eventId}/rsvp/${rsvpId}`, method: "DELETE" }),
      invalidatesTags: (result, error, { eventId }) => [{ type: "Event", id: eventId }, { type: "Event", id: `rsvps-${eventId}` }, "Event"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to cancel RSVP",
      }),
    }),

    getEventStats: builder.query({
      query: (id) => ({ url: `/events/${id}/stats`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Event", id: `stats-${id}` }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch event stats",
      }),
    }),

    // Page Content Management
    getPageContent: builder.query({
      query: (pageKey) => ({
        url: `/pages/${pageKey}`,
        method: "GET",
      }),
      providesTags: ["PageContent"],
    }),

    getPageSection: builder.query({
      query: ({ pageKey, sectionKey }) => ({
        url: `/pages/${pageKey}/${sectionKey}`,
        method: "GET",
      }),
    }),

    upsertPageSection: builder.mutation({
      query: ({ pageKey, sectionKey, content }) => ({
        url: `/pages/${pageKey}/${sectionKey}`,
        method: "POST",
        body: { content },
      }),
      invalidatesTags: ["PageContent"],
    }),

    // Forms
    getForms: builder.query({
      query: () => "/forms",
      providesTags: ["Form"],
    }),
    getFormById: builder.query({
      query: (id) => `/forms/${id}`,
      providesTags: ["Form"],
    }),
    getFormByIdPublic: builder.query({
      query: (id) => `/forms/${id}/public`,
      providesTags: ["Form"],
    }),
    createForm: builder.mutation({
      query: (body) => ({ url: "/forms", method: "POST", body }),
      invalidatesTags: ["Form"],
    }),
    updateForm: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/forms/${id}`, method: "PUT", body }),
      invalidatesTags: ["Form"],
    }),
    deleteForm: builder.mutation({
      query: (id) => ({ url: `/forms/${id}`, method: "DELETE" }),
      invalidatesTags: ["Form"],
    }),
    getFormSubmissions: builder.query({
      query: (formId) => `/forms/${formId}/submissions`,
      providesTags: ["FormSubmission"],
    }),
    submitForm: builder.mutation({
      query: ({ formId, ...body }) => ({
        url: `/forms/${formId}/submit`,
        method: "POST",
        body,
      }),
    }),

    // Newsletter CRUD Operations
    getNewsletters: builder.query({
      query: ({ year, status, page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        if (year) params.append("year", year);
        if (status) {
          params.append("status", status);
        }
        params.append("page", page);
        params.append("limit", limit);

        return {
          url: `/newsletters?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Newsletter"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch newsletters",
      }),
    }),

    getFeaturedNewsletters: builder.query({
      query: ({ limit = 5 } = {}) => {
        const params = new URLSearchParams();
        params.append("limit", limit);

        return {
          url: `/newsletters/featured?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Newsletter"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch featured newsletters",
      }),
    }),

    getNewsletterMonths: builder.query({
      query: ({ year } = {}) => {
        const params = new URLSearchParams();
        if (year) params.append("year", year);

        return {
          url: `/newsletters/months?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Newsletter"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch newsletter months",
      }),
    }),

    getNewslettersByCategory: builder.query({
      query: ({ category, page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);

        return {
          url: `/newsletters/category/${category}?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Newsletter"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch newsletters by category",
      }),
    }),

    getNewsletterByNewsletterId: builder.query({
      query: (newsletterId) => ({
        url: `/newsletters/newsletter-id/${newsletterId}`,
        method: "GET",
      }),
      providesTags: (result, error, newsletterId) => [{ type: "Newsletter", id: newsletterId }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch newsletter",
      }),
    }),

    getNewsletterById: builder.query({
      query: (id) => ({
        url: `/newsletters/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Newsletter", id }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch newsletter",
      }),
    }),

    createNewsletter: builder.mutation({
      query: (newsletterData) => ({
        url: "/newsletters",
        method: "POST",
        body: newsletterData,
      }),
      invalidatesTags: ["Newsletter"],
    }),

    updateNewsletter: builder.mutation({
      query: ({ id, ...updates }) => ({
        url: `/newsletters/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Newsletter", id },
        "Newsletter",
      ],
    }),

    deleteNewsletter: builder.mutation({
      query: (id) => ({
        url: `/newsletters/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Newsletter", id }, "Newsletter"],
    }),
    // Like a newsletter
    likeNewsletter: builder.mutation({
      query: (id) => ({
        url: `/newsletters/${id}/like`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Newsletter", id },
        "Newsletter",
      ],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.error || "Failed to like newsletter",
      }),
    }),

    // Dislike a newsletter
    dislikeNewsletter: builder.mutation({
      query: (id) => ({
        url: `/newsletters/${id}/dislike`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Newsletter", id },
        "Newsletter",
      ],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.error || "Failed to dislike newsletter",
      }),
    }),

    // Item Management
    getItems: builder.query({
      query: ({ page, limit } = {}) => ({
        url: `/items?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["Item"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch items",
      }),
    }),

    getItemById: builder.query({
      query: (id) => ({
        url: `/items/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Item", id }],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to fetch item",
      }),
    }),

    createItem: builder.mutation({
      query: (itemData) => ({
        url: "/items",
        method: "POST",
        body: itemData,
      }),
      invalidatesTags: ["Item"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to create item",
      }),
    }),

    updateItem: builder.mutation({
      query: ({ id, ...itemData }) => ({
        url: `/items/${id}`,
        method: "PUT",
        body: itemData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Item", id },
        "Item",
      ],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to update item",
      }),
    }),

    deleteItem: builder.mutation({
      query: (id) => ({
        url: `/items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Item", id }, "Item"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to delete item",
      }),
    }),

  }),
});

// Export all API hooks for use in components
export const {
  // Admin authentication
  useAdminLoginMutation,

  // Admin management
  useLazyGetAdminsQuery,
  useCreateAdminMutation,
  useGetAdminByIdQuery,
  useUpdateAdminMutation,
  useDeleteAdminMutation,

  // Industries
  useLazyGetIndustriesQuery,

  // Business operations
  useLazyGetBusinessesQuery,
  useLazyGetSponserBusinessesQuery,
  useGetBusinessByIdQuery,
  useLazyGetBusinessByItsQuery,
  useCreateBusinessMutation,
  useUpdateBusinessMutation,
  useDeleteBusinessMutation,

  // Contact form operations
  useSubmitContactFormMutation,
  useGetContactsQuery,
  useUpdateContactStatusMutation,
  useDeleteContactMutation,
  useGetContactByIdQuery,

  // Resource operations
  useLazyGetResourcesQuery,
  useGetResourcesByTypeQuery,
  useLazyGetResourcesByTypeQuery,
  useGetResourceByIdQuery,
  useGetResourceBySlugQuery,
  useGetContentTypeCountsQuery,
  useGetFeaturedResourcesQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  useDeleteResourceMutation,

  // Media operations
  useLazyGetMediaQuery,
  useGetMediaByIdQuery,
  useGetMediaFoldersQuery,
  useUploadMediaMutation,
  useUpdateMediaMutation,
  useDeleteMediaMutation,

  // Event operations
  useLazyGetEventsQuery,
  useGetUpcomingEventsQuery,
  useGetFeaturedEventsQuery,
  useGetEventBySlugQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useRsvpToEventMutation,
  useLazyGetEventRsvpsQuery,
  useCancelRsvpMutation,
  useGetEventStatsQuery,

  // Page content operations
  useGetPageContentQuery,
  useLazyGetPageContentQuery,
  useGetPageSectionQuery,
  useUpsertPageSectionMutation,

  // Newsletter operations
  useLazyGetNewslettersQuery,
  useGetFeaturedNewslettersQuery,
  useLazyGetNewsletterMonthsQuery,
  useLazyGetNewslettersByCategoryQuery,
  useLazyGetNewsletterByNewsletterIdQuery,
  useGetNewsletterByIdQuery,
  useCreateNewsletterMutation,
  useUpdateNewsletterMutation,
  useDeleteNewsletterMutation,
  // Newsletter reactions
  useLikeNewsletterMutation,
  useDislikeNewsletterMutation,

  // Form operations
  useGetFormsQuery,
  useGetFormByIdQuery,
  useGetFormByIdPublicQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useGetFormSubmissionsQuery,
  useSubmitFormMutation,

  // Item management
  useLazyGetItemsQuery,
  useGetItemByIdQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,

} = businessApi;
